import { Box, Button, Input, Stack, Typography, useTheme } from "@mui/material";

import {
  CircleArrowDown,
  RestaurantHeader,
  SearchIcon,
  useDeviceDimensions,
} from "igfd-ui/src";

import { useRestaurantContext } from "../../hooks/useRestaurantContext";

export const SEARCH_PLACEHOLDER = "Search dishes";

interface StaffMenuBannerProps {
  filter: string;
  onFilterChange: (filter: string) => void;
  onMenuOpen: () => void;
  onSignOut: () => void;
}

export const StaffMenuBanner = ({
  filter,
  onFilterChange,
  onMenuOpen,
  onSignOut,
}: StaffMenuBannerProps) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  const { logoImageUrl, menu, brandName, onMenuSelect } =
    useRestaurantContext();
  const { isMobile } = useDeviceDimensions();

  const handleSignOut = () => {
    onMenuSelect?.(undefined);
    onSignOut();
  };

  const signOut = (
    <Button variant="text" onClick={handleSignOut}>
      <Typography color={oscar.light.content.link} variant="bodyRLight">
        Sign out
      </Typography>
    </Button>
  );

  return (
    <Stack
      alignItems="center"
      bgcolor={oscar.light.surface.clear}
      border={`1px solid ${oscar.light.borders.gray}`}
      display="flex"
      justifyContent="space-between"
      direction={{ xs: "column", md: "row" }}
      gap={{ xs: 1, md: 0 }}
      padding={{ xs: 2, md: 4 }}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      {/* On a narrow screen Sign out dropped below the search with nothing to
          line up against, so it sits beside the restaurant name instead. */}
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        width={{ xs: "100%", md: "auto" }}
      >
        <RestaurantHeader
          brandName={brandName}
          logoImageUrl={logoImageUrl}
          logoSize={"lg"}
        />
        {isMobile ? signOut : null}
      </Stack>

      <Stack
        alignItems="center"
        bgcolor={oscar.light.surface.neutral}
        border={`1px solid ${oscar.light.icon.mid}`}
        borderRadius="24px"
        display="flex"
        direction="row"
        flex={1}
        gap={1}
        minHeight={52}
        paddingX={1.5}
        marginX={{ xs: 0, md: 12 }}
        maxWidth={{ xs: "100%", md: 700 }}
        width="100%"
      >
        <Box height={18} width={18}>
          <SearchIcon
            color={oscar.light.content.content1}
            height={18}
            width={18}
          />
        </Box>
        <Input
          disableUnderline
          placeholder={SEARCH_PLACEHOLDER}
          sx={{
            ...typography.labelRMedium,
            width: "100%",
          }}
          onChange={(e) => onFilterChange(e.target.value)}
          value={filter}
        />
        {filter.length > 0 ? (
          <Button variant="text" onClick={() => onFilterChange("")}>
            <Typography variant="bodyRLight" color={oscar.light.content.link}>
              Clear
            </Typography>
          </Button>
        ) : null}

        <Stack
          alignItems="center"
          direction="row"
          gap={1}
          sx={{
            cursor: "pointer",
            maxWidth: 180,
          }}
          onClick={onMenuOpen}
        >
          <Typography
            variant="labelRMedium"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {menu?.menuName}
          </Typography>
          <Box height={22} width={22}>
            <CircleArrowDown
              color={oscar.light.content.link}
              height={20}
              width={20}
            />
          </Box>
        </Stack>
      </Stack>

      {isMobile ? null : <Stack sx={{ width: 120 }}>{signOut}</Stack>}
    </Stack>
  );
};
